import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { Logo } from "@/components/Logo";

const links = [
  { label: "Index", to: "/#top", testid: NAV.linkHome },
  { label: "Shop", to: "/shop", testid: NAV.linkShop },
  { label: "Categories", to: "/#categories", testid: NAV.linkAbout },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openCart, totalItems } = useCart();
  const { me } = useCustomerAuth() || {};
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile menu on route change
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <motion.header
      data-testid={NAV.root}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1], delay: 0.1 }}
      className={`sticky top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-hairline" : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="px-4 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group shrink-0">
          <Logo variant="wordmark" />
          <span className="hidden lg:inline overline text-forest/60 ml-2 border-l border-hairline pl-3">EST · MUMBAI</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 lg:gap-9">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className="edit-link overline text-forest/80 hover:text-forest">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          <CurrencySwitcher className="hidden md:block" />
          <Link
            to="/shop"
            data-testid={NAV.ctaQuote}
            className="hidden lg:inline-flex items-center gap-2 group overline text-forest border border-forest/70 rounded-full px-4 py-2 hover:bg-forest hover:text-white transition-colors duration-500"
          >
            Wholesale Quote
            <span aria-hidden className="inline-block group-hover:translate-x-1 transition-transform duration-500">→</span>
          </Link>

          <button
            data-testid={NAV.cartButton}
            onClick={openCart}
            aria-label="Open cart"
            className="relative p-2 text-forest hover:text-gold transition-colors duration-500"
          >
            <ShoppingBag size={20} strokeWidth={1.4} />
            {totalItems > 0 && (
              <span
                data-testid={NAV.cartCount}
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-white text-[10px] font-medium flex items-center justify-center tabnum"
              >
                {totalItems}
              </span>
            )}
          </button>

          <Link
            to={me ? "/account" : "/account/login"}
            data-testid="nav-account"
            aria-label={me ? "Account" : "Sign in"}
            className="hidden md:inline-flex items-center gap-2 overline text-forest border border-hairline rounded-full px-3 py-2 hover:bg-cream transition-colors"
          >
            <User size={13} strokeWidth={1.5} />
            <span className="text-[10px]">{me ? (me.name?.split(" ")[0] || "Account") : "Sign in"}</span>
          </Link>

          <button
            data-testid={NAV.menuToggle}
            className="md:hidden p-2 -mr-2 text-forest"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-hairline"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} data-testid={l.testid} onClick={() => setOpen(false)} className="font-display text-3xl text-forest">
                  {l.label}
                </Link>
              ))}
              <Link
                to={me ? "/account" : "/account/login"}
                data-testid="nav-account-mobile"
                onClick={() => setOpen(false)}
                className="font-display text-3xl text-forest"
              >
                {me ? "My Account" : "Sign in"}
              </Link>
              <div className="mt-2 flex items-center justify-between border-t border-hairline pt-4">
                <CurrencySwitcher />
                <Link
                  to="/shop"
                  data-testid={NAV.ctaQuote}
                  onClick={() => setOpen(false)}
                  className="inline-flex overline text-forest border border-forest rounded-full px-4 py-3"
                >
                  Wholesale Quote →
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
